import { useId, type ReactNode } from 'react';
import { Lock, type LucideIcon } from 'lucide-react';
import GateAction, { type GateActionProps } from './GateAction';

/**
 * The "this is locked" card: a title with its icon, one honest sentence and the
 * way in. Generalised from the special-deals subscriber lock
 * (pages/special-deals/page.tsx) so every gate looks and reads the same.
 *
 * - `tone="gold"` marks a paid benefit (THEME §2.4c: gold = distinction);
 *   `tone="teal"` (default) marks an account-type gate.
 * - `children` sits between the text and the actions, for a short preview of
 *   what the locked page offers.
 * - The icon sits inline in the title line in the tone's colour. An icon tile
 *   above the heading (owner, 2026-10-07) is one of the "AI-made page" tells.
 */
export type LockedCardAction = Omit<GateActionProps, 'variant' | 'size' | 'className'>;

export interface LockedCardProps {
  /** A lucide icon, or a Remix icon class such as 'ri-lock-line'. Default: a lock. */
  icon?: LucideIcon | string;
  tone?: 'gold' | 'teal';
  title: string;
  body?: ReactNode;
  primary?: LockedCardAction;
  secondary?: LockedCardAction;
  children?: ReactNode;
  /** 2 when the card is the page's main message (teal heading), 3 inside a section (ink). */
  headingLevel?: 2 | 3;
  /** 'md' fits a sentence and two buttons; 'lg' leaves room for a preview in `children`. */
  size?: 'md' | 'lg';
  className?: string;
}

const ICON_TONES: Record<NonNullable<LockedCardProps['tone']>, string> = {
  gold: 'text-[#8C6A0B] dark:text-[#FBD35A]',
  teal: 'text-[var(--pv-color-primary)]',
};

export default function LockedCard({
  icon = Lock,
  tone = 'teal',
  title,
  body,
  primary,
  secondary,
  children,
  headingLevel = 2,
  size = 'md',
  className = '',
}: LockedCardProps) {
  const titleId = useId();
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  // THEME §2.8: h2 carries the brand teal (structure); h3 and below stay ink.
  const headingColour =
    headingLevel === 3 ? 'text-[var(--pv-color-text)]' : 'text-[var(--pv-color-primary)]';

  let iconNode: ReactNode;
  if (typeof icon === 'string') {
    iconNode = <i className={`${icon} text-[1.25rem] leading-none`} aria-hidden="true" />;
  } else {
    const Icon = icon;
    iconNode = <Icon className="h-5 w-5" aria-hidden="true" />;
  }

  return (
    <section
      aria-labelledby={titleId}
      className={[
        'mx-auto w-full rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)]',
        'p-6 text-center shadow-brand-sm sm:p-8',
        size === 'lg' ? 'max-w-2xl' : 'max-w-md',
        className,
      ].join(' ')}
    >
      <Heading id={titleId} className={`flex items-center justify-center gap-2 text-lg font-bold leading-snug ${headingColour}`}>
        <span className={`shrink-0 ${ICON_TONES[tone]}`}>{iconNode}</span>
        {title}
      </Heading>

      {body ? (
        <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">{body}</p>
      ) : null}

      {children ? <div className="mt-5 text-start">{children}</div> : null}

      {primary || secondary ? (
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-center">
          {primary ? <GateAction {...primary} variant="primary" className="w-full sm:w-auto" /> : null}
          {secondary ? <GateAction {...secondary} variant="secondary" className="w-full sm:w-auto" /> : null}
        </div>
      ) : null}
    </section>
  );
}
